import {
    AdsenseCell,
    BounceRate,
    DefaultCellRightAlign,
    IndexCell,
    PagesPerVisit,
    PercentageBarCellRoundedRight,
    RankCell,
    TimeCell,
    TrafficShare,
} from "app/components/React/Table/cells";
import { CategoryCell } from "app/components/React/Table/cells/category-cell/CategoryCell";
import { SegmentOrDomainCellWithoutDomainSign } from "app/components/React/Table/cells/SegmentOrDomainCell";
import {
    DefaultCellHeader,
    DefaultCellHeaderRightAlign,
    DefaultEllipsisHeaderCell,
} from "app/components/React/Table/headerCells";
import { PercentageChangeCell } from "app/components/React/TopSites/DomainsTable/PercentageChangeCell";
import { RankChangeCell } from "app/components/React/TopSites/DomainsTable/website-rankings-momentum/components/RankChangeCell";
import { translate } from "app/filters/i18nFilter";
import { devicesTypes, type DeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { EPeriodSupport } from "app/UtilitiesAndConstants/UtilityFunctions/periodSupport";

const getRankChangeColumn = () => ({
    field: "RankChange",
    displayName: translate("website.rankings.columns.rank.change.title"),
    sortable: false,
    groupable: false,
    cellComponent: RankChangeCell,
    headerComponent: DefaultCellHeaderRightAlign,
    tooltip: translate("website.rankings.columns.rank.change.tooltip"),
    width: 130,
    isResizable: true,
});

const DEFAULT_SORT_FIELD = "TotalShare";
const DEFAULT_SORT_DIRECTION = "desc";

export const DesktopMobileShare = "DesktopMobileShare";

const getDomainColumns = (hasSegments: boolean) => {
    return {
        field: "Domain",
        width: 185,
        sortable: true,
        showTotalCount: true,
        displayName: translate("analysis.source.referrals.table.columns.domain.title"),
        tooltip: hasSegments
            ? "widget.table.tooltip.topsites.domain.or.segment"
            : "widget.table.tooltip.topsites.domain",
        isResizable: true,
        cellComponent: SegmentOrDomainCellWithoutDomainSign,
        fixed: true,
    };
};

export const getBonusCountryColumns = (
    excludeFields = [],
    sortedColumn = { field: DEFAULT_SORT_FIELD, sortDirection: DEFAULT_SORT_DIRECTION },
    hasSegments: boolean,
    isTopLevelCategory: boolean = false,
) => {
    const columns = [
        {
            fixed: true,
            cellComponent: IndexCell,
            sortable: false,
            width: 45,
            dashboardHide: "true",
            disableHeaderCellHover: true,
        },
        { ...getDomainColumns(hasSegments) },
        {
            field: "Share",
            displayName: translate("common.tables.columns.traffic.share.title"),
            sortable: true,
            groupable: false,
            cellComponent: TrafficShare,
            tooltip: "widget.table.tooltip.topsites.share",
            minWidth: 150,
        },
        getRankChangeColumn(),
        !hasSegments &&
            isTopLevelCategory && {
                field: "Category",
                displayName: translate("common.industry"),
                sortable: true,
                groupable: false,
                cellComponent: CategoryCell,
                tooltip: "widget.table.tooltip.website_rankings.industry",
                minWidth: 185,
            },
        !hasSegments && {
            field: "RootCategoryRank",
            displayName: translate("common.tables.columns.industry.rank.title"),
            sortable: true,
            groupable: false,
            cellComponent: RankCell,
            headerComponent: DefaultCellHeaderRightAlign,
            tooltip: "widget.table.tooltip.website_rankings.industry_rank",
            minWidth: 130,
            inverted: true,
        },
    ]
        .filter((column) => !!column && !excludeFields.includes(column.field))
        .map((col) => {
            const isSorted = sortedColumn && col.field === sortedColumn.field;
            return {
                ...col,
                visible: true,
                isSorted: isSorted,
                sortDirection: isSorted ? sortedColumn.sortDirection : DEFAULT_SORT_DIRECTION,
            };
        });
    return columns;
};

export const getColumns = (
    excludeFields = [],
    sortedColumn = { field: DEFAULT_SORT_FIELD, sortDirection: DEFAULT_SORT_DIRECTION },
    periodSupport: EPeriodSupport | null,
    hasSegments: boolean,
    isTopLevelCategory: boolean = false,
    webSource: DeviceType,
) => {
    const periodChangeColumn = periodSupport
        ? {
              field: "PeriodChange",
              displayName:
                  periodSupport === EPeriodSupport.Yearly
                      ? translate("analysis.source.search.all.table.columns.yearly.change.title")
                      : translate("analysis.source.search.all.table.columns.quarterly.change.title"),
              sortable: true,
              groupable: false,
              cellComponent: PercentageChangeCell,
              tooltip:
                  periodSupport === EPeriodSupport.Yearly
                      ? translate("analysis.source.search.all.table.columns.yearly.change.tooltip")
                      : translate("analysis.source.search.all.table.columns.quarterly.change.tooltip"),
              width: periodSupport === EPeriodSupport.Yearly ? 118 : 130,
          }
        : null;
    const isPeriodChangeShouldAppear = Boolean(periodChangeColumn) && periodSupport !== EPeriodSupport.Monthly;

    const columns = [
        {
            fixed: true,
            cellComponent: IndexCell,
            sortable: false,
            width: 65,
            dashboardHide: "true",
            disableHeaderCellHover: true,
        },
        { ...getDomainColumns(hasSegments) },
        {
            field: "Share",
            displayName: translate("common.tables.columns.traffic.share.title"),
            type: "string",
            format: "percentagesign",
            fixed: false,
            sortable: true,
            cellComponent: TrafficShare,
            headerComponent: DefaultCellHeader,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.topsites.share"),
            minWidth: 143,
            isResizable: false,
        },
        {
            field: "MoMChange",
            displayName: translate("analysis.source.search.all.table.columns.month.over.month.change.title"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: PercentageChangeCell,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.topsites.change"),
            width: 158,
            isResizable: true,
        },
        getRankChangeColumn(),
        !hasSegments &&
            isTopLevelCategory && {
                field: "Category",
                displayName: translate("common.industry"),
                type: "string",
                format: "None",
                fixed: false,
                sortable: true,
                groupable: false,
                cellComponent: CategoryCell,
                showTotalCount: false,
                tooltip: translate("widget.table.tooltip.website_rankings.industry"),
                minWidth: 185,
                isResizable: true,
            },
        !hasSegments && {
            field: "RootCategoryRank",
            displayName: translate("common.tables.columns.industry.rank.title"),
            fixed: false,
            sortable: true,
            groupable: false,
            cellComponent: RankCell,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.website_rankings.industry_rank"),
            width: 130,
            isResizable: true,
            inverted: true,
        },
        {
            field: "AvgMonthVisits",
            displayName: translate("common.tables.columns.monthly.visits.title"),
            type: "double",
            format: "minVisitsAbbr",
            fixed: false,
            sortable: true,
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: DefaultCellRightAlign,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.monthlyvisits"),
            width: 130,
            isResizable: true,
        },
        {
            field: "UniqueUsers",
            displayName: translate("industry.trends.table.columns.unique.users"),
            type: "double",
            format: "minVisitsAbbr",
            fixed: false,
            sortable: true,
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: DefaultCellRightAlign,
            showTotalCount: false,
            tooltip: translate("industry.trends.table.columns.unique.users.tooltip"),
            width: 155,
            isResizable: true,
            visible: webSource !== devicesTypes.TOTAL,
        },
        webSource === devicesTypes.TOTAL && {
            field: "DeduplicatedAudience",
            displayName: translate("industry.trends.table.columns.deduplicated.audience"),
            type: "double",
            format: "minVisitsAbbr",
            fixed: false,
            sortable: true,
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: DefaultCellRightAlign,
            showTotalCount: false,
            tooltip: translate("industry.trends.table.columns.unique.users.tooltip"),
            width: 155,
            isResizable: true,
        },
        isPeriodChangeShouldAppear && periodChangeColumn,
        !hasSegments && {
            field: DesktopMobileShare,
            displayName: translate("common.tables.columns.desktop.mobile.share.title"),
            sortable: "False",
            groupable: false,
            cellComponent: PercentageBarCellRoundedRight,
            headerComponent: DefaultCellHeaderRightAlign,
            tooltip: "wa.ao.desktopvsmobile.tooltip",
            minWidth: 190,
        },
        {
            field: "AvgVisitDuration",
            displayName: translate("common.tables.columns.visit.duration.title"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: TimeCell,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.topsites.avgvisitduration"),
            width: 130,
            isResizable: true,
        },
        {
            field: "PagesPerVisit",
            displayName: translate("common.tables.columns.page.per.visit.title"),
            fixed: false,
            sortable: true,
            type: "double",
            format: "number",
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: PagesPerVisit,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.topsites.ppv"),
            width: 120,
            isResizable: true,
        },
        {
            field: "BounceRate",
            displayName: translate("common.tables.columns.bounce.rate.title"),
            type: "double",
            format: "number",
            fixed: false,
            sortable: true,
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: BounceRate,
            showTotalCount: false,
            tooltip: translate("widget.table.tooltip.topsites.bouncerate"),
            width: 125,
            isResizable: true,
            inverted: true,
        },
        {
            field: "HasAdsense",
            displayName: translate("common.tables.columns.adsense.title"),
            type: "bool",
            format: "None",
            fixed: false,
            sortable: true,
            headerComponent: DefaultEllipsisHeaderCell,
            cellComponent: AdsenseCell,
            showTotalCount: false,
            width: 100,
            isResizable: true,
        },
    ]
        .filter((column) => {
            return !!column && !excludeFields.includes(column.field);
        })
        .map((col) => {
            const isSorted = sortedColumn && col.field === sortedColumn.field;
            return {
                visible: true,
                ...col,
                isSorted: isSorted,
                sortDirection: isSorted ? sortedColumn.sortDirection : DEFAULT_SORT_DIRECTION,
            };
        });
    return columns;
};
